import { c as _c } from "react/compiler-runtime";
import { useQuery } from "react-query";
import { useApi } from "./useApi";
import { useOrgFromPathname } from "./useOrgFromPathname";
import { useConnectedGithubOrgsNonSuspense } from "./useConnectedGithubOrgs";
export interface CachedAssistantSession {
  id: string;
  user_id: number | null;
  user_name: string | null;
  title: string | null;
  status: string;
  source: string;
  total_messages: number;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  managed_prs?: Array<{
    pr_number: number;
    status: "open" | "draft" | "merged" | "closed";
    repository: string;
  }>;
}
export interface SearchedSession extends CachedAssistantSession {
  isMine: boolean;
}
const SESSION_CACHE_LIMIT = 100;
const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const MAX_SEARCH_RESULTS = 8;
const MAX_RECENT_SESSIONS = 3;
export function useAssistantSessionCache() {
  const $ = _c(14);
  const api = useApi();
  const activeGithubOrg = useOrgFromPathname();
  const {
    connectedGithubOrgs
  } = useConnectedGithubOrgsNonSuspense();
  let t0;
  if ($[0] !== activeGithubOrg || $[1] !== connectedGithubOrgs?.installations) {
    t0 = connectedGithubOrgs?.installations.find(installation => installation?.account?.login === activeGithubOrg)?.codesmith_enabled ?? false;
    $[0] = activeGithubOrg;
    $[1] = connectedGithubOrgs?.installations;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const codesmithEnabled = t0;
  let t1;
  if ($[3] !== activeGithubOrg) {
    t1 = ["assistantSessionCache", activeGithubOrg];
    $[3] = activeGithubOrg;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== api) {
    t2 = async () => {
      const response = await api.get(`/assistant/sessions?status=active&limit=${SESSION_CACHE_LIMIT}`);
      return (response.data.sessions || []) as CachedAssistantSession[];
    };
    $[5] = api;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = !!activeGithubOrg && codesmithEnabled;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      enabled: t3,
      staleTime: STALE_TIME,
      cacheTime: STALE_TIME * 2,
      refetchOnWindowFocus: false
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data,
    isLoading
  } = useQuery(t1, t2, t4);
  let t5;
  if ($[9] !== data) {
    t5 = data || [];
    $[9] = data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isLoading || $[12] !== t5) {
    t6 = {
      sessions: t5,
      isLoading
    };
    $[11] = isLoading;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
export function searchSessions(searchTerm: string, sessions: CachedAssistantSession[], limit?: number, opts?: {
  userId?: number;
}): SearchedSession[] {
  if (!searchTerm || searchTerm.length < 2) return [];
  const effectiveLimit = limit ?? MAX_SEARCH_RESULTS;
  const userId = opts?.userId;
  const lowerSearch = searchTerm.toLowerCase();
  return sessions.filter(session => session.status === "active" && session.title && session.title.toLowerCase().includes(lowerSearch)).map(session => ({
    ...session,
    isMine: userId != null && session.user_id === userId
  })).sort((a, b) => {
    // Prioritize my sessions over team sessions
    if (a.isMine !== b.isMine) return a.isMine ? -1 : 1;
    return 0;
  }).slice(0, effectiveLimit);
}
function sessionRecencyMs(session: CachedAssistantSession): number {
  return new Date(session.last_message_at || session.updated_at).getTime();
}

// Most recently active sessions, with the user's own sessions first.
// Used to surface recent Codesmith sessions in the command palette before
// the user types a search query.
export function getRecentSessions(sessions: CachedAssistantSession[], limit?: number, opts?: {
  userId?: number;
}): SearchedSession[] {
  const effectiveLimit = limit ?? MAX_RECENT_SESSIONS;
  const userId = opts?.userId;
  const active = sessions.filter(session => session.status === "active").map(session => ({
    ...session,
    isMine: userId != null && session.user_id === userId
  })).sort((a, b) => sessionRecencyMs(b) - sessionRecencyMs(a));

  // My sessions take precedence; fill any remaining slots with the most
  // recent team sessions.
  const mine = active.filter(session => session.isMine);
  const team = active.filter(session => !session.isMine);
  return [...mine, ...team].slice(0, effectiveLimit);
}